import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Toaster, toast } from "react-hot-toast";
import {
  deleteNotificationTemplate,
  getNotificationTemplates,
  upsertNotificationTemplate,
} from "../utils/templateStorage";

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeText = (value) => String(value || "").trim();

const getTheme = (channel) =>
  channel === "email"
    ? {
        badgeClass: "border-blue-200 bg-blue-100 text-blue-700",
        panelClass: "from-blue-50 via-white to-indigo-50",
        buttonClass: "bg-blue-600 hover:bg-blue-700",
        previewClass: "border-blue-200 bg-blue-50",
      }
    : {
        badgeClass: "border-amber-200 bg-amber-100 text-amber-700",
        panelClass: "from-amber-50 via-white to-orange-50",
        buttonClass: "bg-amber-500 hover:bg-amber-600",
        previewClass: "border-amber-200 bg-amber-50",
      };

const createInitialForm = (channel) => ({
  templateName: "",
  subject: "",
  body: "",
  deliveryType: channel === "email" ? "email" : "sms",
});

const TemplateManager = ({
  channel,
  badgeText,
  heading,
  description,
  subjectLabel,
  bodyLabel,
  bodyPlaceholder,
  simpleHelpText,
}) => {
  const theme = getTheme(channel);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(createInitialForm(channel));

  const isSmsPhonePage = channel === "sms-phone";

  const refreshTemplates = () => {
    setTemplates(getNotificationTemplates(channel));
  };

  useEffect(() => {
    refreshTemplates();
  }, [channel]);

  const resetForm = () => {
    setEditingId("");
    setForm(createInitialForm(channel));
  };

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!normalizeText(form.templateName)) {
      toast.error("Please enter template name");
      return;
    }

    if (!normalizeText(form.subject)) {
      toast.error(`Please enter ${subjectLabel.toLowerCase()}`);
      return;
    }

    if (!normalizeText(form.body)) {
      toast.error(`Please enter ${bodyLabel.toLowerCase()}`);
      return;
    }

    upsertNotificationTemplate({
      id: editingId || undefined,
      channel,
      deliveryType: isSmsPhonePage ? form.deliveryType : "email",
      templateName: normalizeText(form.templateName),
      subject: normalizeText(form.subject),
      body: form.body,
      status: "active",
      module: "customer",
      target: isSmsPhonePage ? "message-template" : "email-template",
      secondaryField: "",
      variables: [],
      notes: "",
    });

    toast.success(editingId ? "Template updated" : "Template saved");
    resetForm();
    refreshTemplates();
  };

  const handleEdit = (template) => {
    setEditingId(template?.id || "");
    setForm({
      templateName: template?.templateName || "",
      subject: template?.subject || "",
      body: template?.body || "",
      deliveryType: template?.deliveryType || (channel === "email" ? "email" : "sms"),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (templateId) => {
    deleteNotificationTemplate(templateId);
    if (String(editingId) === String(templateId)) {
      resetForm();
    }
    toast.success("Template deleted");
    refreshTemplates();
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className={`border-b border-slate-200 bg-gradient-to-r ${theme.panelClass} p-5 sm:p-6`}>
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${theme.badgeClass}`}>
            {badgeText}
          </span>
          <h3 className="mt-3 text-xl font-semibold text-slate-900">{heading}</h3>
          <p className="mt-1 text-sm text-slate-600">{description}</p>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-800">How to use</p>
            <p className="mt-2 text-sm text-slate-600">{simpleHelpText}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_320px] sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Template Name</label>
              <input
                type="text"
                value={form.templateName}
                onChange={(event) => handleChange("templateName", event.target.value)}
                placeholder="Example: Booking Confirmation"
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
            </div>

            {isSmsPhonePage ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Template Type</label>
                <select
                  value={form.deliveryType}
                  onChange={(event) => handleChange("deliveryType", event.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                >
                  <option value="sms">SMS Template</option>
                  <option value="phone">Phone Script</option>
                </select>
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">{subjectLabel}</label>
              <input
                type="text"
                value={form.subject}
                onChange={(event) => handleChange("subject", event.target.value)}
                placeholder={channel === "email" ? "Example: Your booking is confirmed" : "Example: Booking Reminder"}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">{bodyLabel}</label>
              <textarea
                rows={8}
                value={form.body}
                onChange={(event) => handleChange("body", event.target.value)}
                placeholder={bodyPlaceholder}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className={`rounded-lg px-5 py-2.5 text-sm font-semibold text-white ${theme.buttonClass}`}
              >
                {editingId ? "Update Template" : "Save Template"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Clear
              </button>
            </div>
          </form>

          <div className={`rounded-2xl border p-4 ${theme.previewClass}`}>
            <h4 className="text-base font-semibold text-slate-900">Simple Preview</h4>
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase text-slate-500">{subjectLabel}</p>
              <p className="mt-1 text-sm text-slate-800">{form.subject || "-"}</p>
            </div>
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase text-slate-500">{bodyLabel}</p>
              <pre className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">
                {form.body || "-"}
              </pre>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-slate-900">Saved Templates</h4>
          <p className="text-sm text-slate-500">Click edit if you want to change any saved template.</p>
        </div>

        {templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
            No templates saved yet.
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <div
                key={template?.id}
                className="rounded-xl border border-slate-200 bg-slate-50 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h5 className="text-sm font-semibold text-slate-900">{template?.templateName || "-"}</h5>
                      {isSmsPhonePage ? (
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${theme.badgeClass}`}>
                          {template?.deliveryType === "phone" ? "Phone" : "SMS"}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-2 text-sm font-medium text-slate-700">{template?.subject || "-"}</p>
                    <p className="mt-1 line-clamp-3 text-sm text-slate-600">{template?.body || "-"}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      Updated: {formatDateTime(template?.updatedAt)}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(template)}
                      className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(template?.id)}
                      className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

TemplateManager.propTypes = {
  channel: PropTypes.string.isRequired,
  badgeText: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  subjectLabel: PropTypes.string.isRequired,
  bodyLabel: PropTypes.string.isRequired,
  bodyPlaceholder: PropTypes.string.isRequired,
  simpleHelpText: PropTypes.string.isRequired,
};

export default TemplateManager;
